import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { useTranslation } from 'react-i18next';
import type { LicenseInfo, UserProfile, UserLicense } from '../types';
import { XIcon, MapPinIcon, CameraIcon, IdCardIcon } from './icons';
import { addUserLicense, updateUserLicense } from '../services/licenses';
import { reverseGeocodeServer } from '../services/geocodeClient';
import { uploadImage } from '../services/imageUpload';
import { getRoddoUiTokens, resolveRoddoDarkMode } from '../constants/theme';

interface LicenseDetailViewProps {
  userProfile: UserProfile;
  isDarkMode?: boolean;
  userLicenses: UserLicense[];
  onLicensesChange: (licenses: UserLicense[]) => void;
  onBack: () => void;
  onStartPurchase: (license: LicenseInfo) => void;
}

function formatDate(value: any): string {
  if (!value) return '-';
  const d =
    value?.toDate?.() instanceof Date
      ? value.toDate()
      : value instanceof Date
        ? value
        : new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString();
}

function toInputDate(value: any): string {
  if (!value) return '';
  const d =
    value?.toDate?.() instanceof Date
      ? value.toDate()
      : value instanceof Date
        ? value
        : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

const isBlobUrl = (url: any) => typeof url === 'string' && url.startsWith('blob:');

async function uploadLicensePhoto(uid: string, licenseId: string, file: File): Promise<string> {
  return uploadImage(file, `licenses/${uid}/${licenseId}.jpg`, {
    maxSizeMB: 1.2,
    maxDim: 1600,
    contentType: 'image/jpeg',
  });
}

const buildFormStateFromLicense = (license: any) => {
  const expiryRaw = license?.expiryDate ?? license?.expiry ?? license?.endDate ?? null;
  return {
    state: license?.jurisdictionState || license?.state || license?.jurisdiction || '',
    type: license?.type || '',
    licenseNumber: license?.licenseNumber || '',
    expiryDate: toInputDate(expiryRaw),
  };
};

const getPersistedPhotoUrl = (license: any): string | null => {
  const raw = license?.photoUrl || license?.licensePhotoUrl || null;
  return isBlobUrl(raw) ? null : raw;
};


type LicenseLocationCoords = { lat: number; lon: number };

const LICENSE_REGION_ALIASES: Array<{ label: string; patterns: string[] }> = [
  { label: 'Alabama', patterns: ['alabama', ' al ', ',al,'] },
  { label: 'Alaska', patterns: ['alaska', ' ak ', ',ak,'] },
  { label: 'Arizona', patterns: ['arizona', ' az ', ',az,'] },
  { label: 'Arkansas', patterns: ['arkansas', ' ar ', ',ar,'] },
  { label: 'California', patterns: ['california', ' ca ', ',ca,'] },
  { label: 'Colorado', patterns: ['colorado', ' co ', ',co,'] },
  { label: 'Connecticut', patterns: ['connecticut', ' ct ', ',ct,'] },
  { label: 'Delaware', patterns: ['delaware', ' de ', ',de,'] },
  { label: 'Florida', patterns: ['florida', ' fl ', ',fl,'] },
  { label: 'Georgia', patterns: ['georgia', ' ga ', ',ga,'] },
  { label: 'Hawaii', patterns: ['hawaii', ' hi ', ',hi,'] },
  { label: 'Idaho', patterns: ['idaho', ' id ', ',id,'] },
  { label: 'Illinois', patterns: ['illinois', ' il ', ',il,'] },
  { label: 'Indiana', patterns: ['indiana', ' in ', ',in,'] },
  { label: 'Iowa', patterns: ['iowa', ' ia ', ',ia,'] },
  { label: 'Kansas', patterns: ['kansas', ' ks ', ',ks,'] },
  { label: 'Kentucky', patterns: ['kentucky', ' ky ', ',ky,'] },
  { label: 'Louisiana', patterns: ['louisiana', ' la ', ',la,'] },
  { label: 'Maine', patterns: ['maine', ' me ', ',me,'] },
  { label: 'Maryland', patterns: ['maryland', ' md ', ',md,'] },
  { label: 'Massachusetts', patterns: ['massachusetts', ' ma ', ',ma,'] },
  { label: 'Michigan', patterns: ['michigan', ' mi ', ',mi,'] },
  { label: 'Minnesota', patterns: ['minnesota', ' mn ', ',mn,'] },
  { label: 'Mississippi', patterns: ['mississippi', ' ms ', ',ms,'] },
  { label: 'Missouri', patterns: ['missouri', ' mo ', ',mo,'] },
  { label: 'Montana', patterns: ['montana', ' mt ', ',mt,'] },
  { label: 'Nebraska', patterns: ['nebraska', ' ne ', ',ne,'] },
  { label: 'Nevada', patterns: ['nevada', ' nv ', ',nv,'] },
  { label: 'New Hampshire', patterns: ['new hampshire', ' nh ', ',nh,'] },
  { label: 'New Jersey', patterns: ['new jersey', ' nj ', ',nj,'] },
  { label: 'New Mexico', patterns: ['new mexico', ' nm ', ',nm,'] },
  { label: 'New York', patterns: ['new york', ' ny ', ',ny,'] },
  { label: 'North Carolina', patterns: ['north carolina', ' nc ', ',nc,'] },
  { label: 'North Dakota', patterns: ['north dakota', ' nd ', ',nd,'] },
  { label: 'Ohio', patterns: ['ohio', ' oh ', ',oh,'] },
  { label: 'Oklahoma', patterns: ['oklahoma', ' ok ', ',ok,'] },
  { label: 'Oregon', patterns: ['oregon', ' or ', ',or,'] },
  { label: 'Pennsylvania', patterns: ['pennsylvania', ' pa ', ',pa,'] },
  { label: 'Rhode Island', patterns: ['rhode island', ' ri ', ',ri,'] },
  { label: 'South Carolina', patterns: ['south carolina', ' sc ', ',sc,'] },
  { label: 'South Dakota', patterns: ['south dakota', ' sd ', ',sd,'] },
  { label: 'Tennessee', patterns: ['tennessee', ' tn ', ',tn,'] },
  { label: 'Texas', patterns: ['texas', ' tx ', ',tx,'] },
  { label: 'Utah', patterns: ['utah', ' ut ', ',ut,'] },
  { label: 'Vermont', patterns: ['vermont', ' vt ', ',vt,'] },
  { label: 'Virginia', patterns: ['virginia', ' va ', ',va,'] },
  { label: 'Washington', patterns: ['washington', ' wa ', ',wa,'] },
  { label: 'West Virginia', patterns: ['west virginia', ' wv ', ',wv,'] },
  { label: 'Wisconsin', patterns: ['wisconsin', ' wi ', ',wi,'] },
  { label: 'Wyoming', patterns: ['wyoming', ' wy ', ',wy,'] },
  { label: 'District of Columbia', patterns: ['district of columbia', 'washington dc', ' dc ', ',dc,'] },
  { label: 'Alberta', patterns: ['alberta', ' ab ', ',ab,'] },
  { label: 'British Columbia', patterns: ['british columbia', ' bc ', ',bc,'] },
  { label: 'Manitoba', patterns: ['manitoba', ' mb ', ',mb,'] },
  { label: 'New Brunswick', patterns: ['new brunswick', ' nb ', ',nb,'] },
  { label: 'Newfoundland and Labrador', patterns: ['newfoundland and labrador', 'newfoundland', ' labrador', ' nl ', ',nl,'] },
  { label: 'Northwest Territories', patterns: ['northwest territories', ' nt ', ',nt,'] },
  { label: 'Nova Scotia', patterns: ['nova scotia', ' ns ', ',ns,'] },
  { label: 'Nunavut', patterns: ['nunavut', ' nu ', ',nu,'] },
  { label: 'Ontario', patterns: ['ontario', ' on ', ',on,'] },
  { label: 'Prince Edward Island', patterns: ['prince edward island', ' pei ', ' pe ', ',pe,'] },
  { label: 'Quebec', patterns: ['quebec', ' québec', ' qc ', ',qc,'] },
  { label: 'Saskatchewan', patterns: ['saskatchewan', ' sk ', ',sk,'] },
  { label: 'Yukon', patterns: ['yukon', ' yt ', ',yt,'] },
];

function buildOfficialLicenseSearchUrl(region?: string | null): string {
  const clean = String(region || '').trim();
  const query = clean
    ? `${clean} fishing license online purchase official`
    : 'buy fishing license online official';
  return `https://www.google.com/search?q=${encodeURIComponent(query)}`;
}

function resolveLicenseRegionFromPlace(label?: string | null): string | null {
  const raw = String(label || '').trim();
  if (!raw) return null;

  const haystack = ` ${raw.toLowerCase().replace(/[.]/g, '').replace(/[,/|]+/g, ' , ')} `;
  const exact = LICENSE_REGION_ALIASES.find((region) =>
    region.patterns.some((pattern) => haystack.includes(pattern)),
  );
  if (exact) return exact.label;

  const parts = raw
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);

  if (parts.length >= 2) {
    const stateish = parts[parts.length - 2];
    const matched = LICENSE_REGION_ALIASES.find(
      (region) => region.label.toLowerCase() === stateish.toLowerCase(),
    );
    if (matched) return matched.label;
  }

  const country = parts[parts.length - 1] || raw;
  return country || null;
}

async function getCurrentLicenseCoords(): Promise<LicenseLocationCoords> {
  const isNative = (() => {
    try {
      return Capacitor.isNativePlatform();
    } catch {
      return false;
    }
  })();

  if (isNative) {
    try {
      const mod = await import('@capacitor/geolocation');
      const { Geolocation } = mod;
      const perms = await Geolocation.checkPermissions();
      if (perms.location !== 'granted') {
        const requested = await Geolocation.requestPermissions({ permissions: ['location'] });
        if (requested.location !== 'granted') throw new Error('Location permission denied');
      }
      const pos = await Geolocation.getCurrentPosition({
        enableHighAccuracy: true,
        timeout: 12000,
        maximumAge: 60000,
      });
      return { lat: pos.coords.latitude, lon: pos.coords.longitude };
    } catch (err) {
      console.warn('[LicenseDetail] native geolocation failed; using browser fallback', err);
    }
  }

  const geo = window.navigator?.geolocation;
  if (!geo) throw new Error('Geolocation is not available');

  return new Promise((resolve, reject) => {
    geo.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lon: pos.coords.longitude }),
      (err) => reject(err || new Error('Could not get current location')),
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 60000 },
    );
  });
}

const LicenseDetailView: React.FC<LicenseDetailViewProps> = ({
  userProfile,
  userLicenses,
  isDarkMode,
  onBack,
  onLicensesChange,
}) => {
  const { t } = useTranslation();
  const isDark = typeof isDarkMode === 'boolean' ? isDarkMode : resolveRoddoDarkMode();
  const ui = getRoddoUiTokens(isDark) as any;

  const activeLicense = useMemo(
    () => userLicenses.find((l) => (l as any).active) ?? userLicenses[0] ?? null,
    [userLicenses],
  );

  const holderName =
    (userProfile as any).name ||
    (userProfile as any).displayName ||
    t('licenseDetail.unknownHolder', 'Unknown holder');

  const avatarSrc = (userProfile as any).avatar || (userProfile as any).photoURL || '';

  const [formState, setFormState] = useState({
    state: '',
    type: '',
    licenseNumber: '',
    expiryDate: '',
  });

  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isFindingLocalLicense, setIsFindingLocalLicense] = useState(false);

  const photoInputRef = useRef<HTMLInputElement>(null);
  const [licensePhotoFile, setLicensePhotoFile] = useState<File | null>(null);
  const [licensePhotoPreview, setLicensePhotoPreview] = useState<string | null>(null);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);

  const [photoLightboxOpen, setPhotoLightboxOpen] = useState(false);

  const persistedPhotoUrl = useMemo(() => getPersistedPhotoUrl(activeLicense), [activeLicense]);

  const revokeIfBlob = (maybeUrl: string | null) => {
    if (maybeUrl?.startsWith('blob:')) {
      try {
        URL.revokeObjectURL(maybeUrl);
      } catch {}
    }
  };

  useEffect(() => {
    return () => {
      revokeIfBlob(licensePhotoPreview);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!activeLicense) {
      setFormState({ state: '', type: '', licenseNumber: '', expiryDate: '' });
      setLicensePhotoFile(null);
      setLicensePhotoPreview(null);
      setIsEditing(false);
      return;
    }

    if (isEditing) return;

    setFormState(buildFormStateFromLicense(activeLicense));

    if (persistedPhotoUrl) {
      setLicensePhotoPreview(persistedPhotoUrl);
      setLicensePhotoFile(null);
    } else {
      setLicensePhotoPreview((prev) => {
        if (isBlobUrl(prev)) revokeIfBlob(prev);
        return null;
      });
      setLicensePhotoFile(null);
    }
  }, [activeLicense, isEditing, persistedPhotoUrl]);

  const licenseRegionName = useMemo(() => {
    return String(
      formState.state ||
        (activeLicense as any)?.jurisdictionState ||
        (activeLicense as any)?.state ||
        (activeLicense as any)?.jurisdiction ||
        '',
    ).trim();
  }, [activeLicense, formState.state]);

  const licenseUrl = useMemo(
    () => buildOfficialLicenseSearchUrl(licenseRegionName),
    [licenseRegionName],
  );

  const openUrl = (url: string, popup?: Window | null) => {
    try {
      if (popup && !popup.closed) {
        popup.location.href = url;
        return;
      }
    } catch {}

    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const openGovSite = async () => {
    if (licenseRegionName) {
      openUrl(licenseUrl);
      return;
    }

    const popup = window.open('', '_blank');
    setIsFindingLocalLicense(true);

    try {
      const coords = await getCurrentLicenseCoords();
      const placeLabel = await reverseGeocodeServer(coords.lat, coords.lon);
      const region = resolveLicenseRegionFromPlace(placeLabel);

      if (region) {
        setFormState((prev) => ({ ...prev, state: region }));
        openUrl(buildOfficialLicenseSearchUrl(region), popup);
        return;
      }

      openUrl(buildOfficialLicenseSearchUrl(null), popup);
    } catch (err) {
      console.warn('[LicenseDetail] could not detect license region', err);
      openUrl(buildOfficialLicenseSearchUrl(null), popup);
    } finally {
      setIsFindingLocalLicense(false);
    }
  };

  const onPickLicensePhoto = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;

    if (!f.type?.startsWith('image/')) {
      alert('Please choose an image file.');
      return;
    }

    if (f.size > 10 * 1024 * 1024) {
      alert('Image must be under 10MB.');
      return;
    }

    setLicensePhotoFile(f);
    const url = URL.createObjectURL(f);

    setLicensePhotoPreview((prev) => {
      if (prev?.startsWith('blob:')) revokeIfBlob(prev);
      return url;
    });
  };

  const deactivateOthersInFirestore = async (uid: string, keepId: string) => {
    const others = (userLicenses || []).filter((l) => (l as any).id && (l as any).id !== keepId);
    await Promise.all(
      others.map((l) => updateUserLicense(uid, (l as any).id, { active: false } as any)),
    );
  };

  const handleSave = async (): Promise<boolean> => {
    if (!userProfile?.uid) return false;

    const stateClean = (formState.state || '').trim();
    const numberClean = (formState.licenseNumber || '').trim();

    if (!stateClean) {
      alert(t('licenseDetail.stateRequired', 'Please enter your State / Region.'));
      return false;
    }

    if (!numberClean) {
      alert(t('licenseDetail.numberRequired', 'Please enter your license / permit number.'));
      return false;
    }

    setSaving(true);

    try {
      const uid = userProfile.uid;
      const typeClean = (
        formState.type ||
        (activeLicense as any)?.type ||
        'Recreational Sport Fishing'
      ).trim();

      const expiryDateClean = formState.expiryDate ? formState.expiryDate : null;

      const payload: Partial<UserLicense> & Record<string, any> = {
        jurisdictionState: stateClean,
        state: stateClean,
        type: typeClean,
        licenseNumber: numberClean,
        active: true,
      };

      if (expiryDateClean) {
        payload.expiryDate = expiryDateClean;
      }

      const existingId = (activeLicense as any)?.id;
      let licenseId = existingId as string | undefined;

      if (licenseId) {
        await updateUserLicense(uid, licenseId, payload);
      } else {
        const createdId = await addUserLicense(uid, payload);
        licenseId = typeof createdId === 'string' ? createdId : (createdId as any)?.id;
      }

      if (!licenseId) {
        throw new Error('License ID was not returned after save.');
      }

      let finalPhotoUrl: string | null =
        (!isBlobUrl((activeLicense as any)?.photoUrl) && (activeLicense as any)?.photoUrl) ||
        (!isBlobUrl((activeLicense as any)?.licensePhotoUrl) && (activeLicense as any)?.licensePhotoUrl) ||
        null;

      if (licensePhotoFile) {
        setIsUploadingPhoto(true);
        try {
          const url = await uploadLicensePhoto(uid, licenseId, licensePhotoFile);

          await updateUserLicense(uid, licenseId, {
            photoUrl: url,
            licensePhotoUrl: url,
          } as any);

          finalPhotoUrl = url;
          setLicensePhotoPreview(url);
          setLicensePhotoFile(null);
        } finally {
          setIsUploadingPhoto(false);
        }
      }

      await deactivateOthersInFirestore(uid, licenseId);
      await updateUserLicense(uid, licenseId, { active: true } as any);

      const next = (userLicenses || []).map((l) => {
        const id = (l as any).id;
        if (!id) return l as any;

        if (id === licenseId) {
          return {
            ...(l as any),
            ...(payload as any),
            id,
            userId: uid,
            active: true,
            photoUrl:
              finalPhotoUrl ?? (isBlobUrl((l as any).photoUrl) ? null : (l as any).photoUrl) ?? null,
            licensePhotoUrl:
              finalPhotoUrl ??
              (isBlobUrl((l as any).licensePhotoUrl) ? null : (l as any).licensePhotoUrl) ??
              null,
          } as any;
        }

        return { ...(l as any), active: false } as any;
      });

      const existsLocally = next.some((l) => (l as any).id === licenseId);
      const finalNext = existsLocally
        ? next
        : [
            {
              id: licenseId,
              userId: uid,
              ...(payload as any),
              active: true,
              photoUrl: finalPhotoUrl ?? null,
              licensePhotoUrl: finalPhotoUrl ?? null,
            } as any,
            ...next.map((l) => ({ ...(l as any), active: false } as any)),
          ];

      onLicensesChange(finalNext);
      return true;
    } catch (err) {
      console.error('[LicenseDetail] Failed to save license:', err);
      alert(t('licenseDetail.saveError', 'Could not save your license details. Please try again.'));
      return false;
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (!isEditing) return;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeEditModal();
    };

    document.addEventListener('keydown', onKey);

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing, persistedPhotoUrl]);

  const openEditModal = () => {
    if (!activeLicense) return;

    setFormState(buildFormStateFromLicense(activeLicense));
    setLicensePhotoFile(null);
    setLicensePhotoPreview((prev) => {
      if (isBlobUrl(prev)) revokeIfBlob(prev);
      return persistedPhotoUrl ?? null;
    });

    setIsEditing(true);
  };

  const closeEditModal = () => {
    setIsEditing(false);
    setLicensePhotoFile(null);
    setLicensePhotoPreview((prev) => {
      if (isBlobUrl(prev)) revokeIfBlob(prev);
      return persistedPhotoUrl ?? null;
    });

    if (activeLicense) {
      setFormState(buildFormStateFromLicense(activeLicense));
    }
  };

  const pageStyle: React.CSSProperties = {
    color: ui.textPrimary,
    background: ui.pageBg ?? 'transparent',
  };
  const panelStyle: React.CSSProperties = {
    background: ui.cardBg,
    border: `1px solid ${ui.border}`,
    boxShadow: ui.shadow,
    color: ui.textPrimary,
  };
  const strongPanelStyle: React.CSSProperties = {
    background: ui.cardBgStrong ?? ui.cardBg,
    border: `1px solid ${ui.borderStrong ?? ui.border}`,
    boxShadow: ui.shadowStrong ?? ui.shadow,
    color: ui.textPrimary,
  };
  const inputStyle: React.CSSProperties = {
    background: ui.surfaceBg ?? ui.cardBgMuted ?? ui.cardBg,
    border: `1px solid ${ui.border}`,
    color: ui.textPrimary,
    boxShadow: ui.insetHighlight,
  };
  const subtleButtonStyle: React.CSSProperties = {
    background: ui.surfaceBg ?? ui.cardBgMuted ?? ui.cardBg,
    border: `1px solid ${ui.border}`,
    color: ui.textPrimary,
    boxShadow: ui.shadow,
  };
  const mintButtonStyle: React.CSSProperties = {
    background: isDark ? 'rgba(112,87,232,0.16)' : 'rgba(232,243,255,0.96)',
    color: isDark ? '#FFFFFF' : '#143226',
    boxShadow: 'none',
    border: `1px solid ${ui.borderStrong ?? ui.border}`, 
  };
  const goldButtonStyle: React.CSSProperties = {
    background: isDark ? 'rgba(246,196,83,0.16)' : 'rgba(255,240,203,0.96)',
    color: '#3A2C0A',
    boxShadow: 'none',
    border: `1px solid ${ui.borderStrong ?? ui.border}`, 
  };

  if (!activeLicense) {
    return (
      <div className="flex h-full flex-col space-y-4 p-4 roddo-license-polish" data-roddo-polish-lane="2" style={pageStyle}>
        <header className="mb-2 flex items-center justify-between">
          <div>
            <p className="text-xs font-bold uppercase" style={{ color: ui.textSecondary }}>
              {t('licenseDetail.title', 'Fishing license')}
            </p>
            <h1 className="text-2xl font-extrabold" style={{ color: ui.textPrimary }}>
              {t('licenseDetail.noLicenseTitle', 'No license on file')}
            </h1>
            <p className="mt-1 text-xs" style={{ color: ui.textSecondary }}>
              {t(
                'licenseDetail.noLicenseSubtitle',
                'Add, review, or renew your fishing license details in one place.',
              )}
            </p>
          </div>

          <button type="button" onClick={onBack} className="rounded-full p-2" style={subtleButtonStyle}>
            <XIcon className="h-4 w-4" />
          </button>
        </header>

        <div className="flex flex-col items-center justify-center space-y-4 p-8 text-center rounded-[20px]" style={strongPanelStyle}>
          <div className="rounded-xl p-2" style={{ ...panelStyle, background: ui.surfaceBg ?? ui.cardBgMuted ?? ui.cardBg }}>
            <IdCardIcon className="h-10 w-10" />
          </div>

          <p className="max-w-xs text-sm" style={{ color: ui.textSecondary }}>
            {t(
              'licenseDetail.noLicenseBody',
              'We couldn’t find any active fishing licenses on your profile.',
            )}
          </p>

          <button type="button" onClick={openGovSite} disabled={isFindingLocalLicense} className="inline-flex items-center gap-2 rounded-xl px-4 py-2 text-xs font-semibold disabled:opacity-60" style={subtleButtonStyle}>
            <MapPinIcon className="h-4 w-4" />
            {isFindingLocalLicense
              ? t('licenseDetail.findingLocalSite', 'Finding your local license…')
              : t('licenseDetail.openLocalSite', 'Find local fishing license')}
          </button>
        </div>

        <div className="mt-2 space-y-4 rounded-[20px] p-4" style={strongPanelStyle}>
          <h2 className="text-[22px] font-extrabold" style={{ color: ui.textPrimary }}>
            {t('licenseDetail.addLicenseHeading', 'Add your license details')}
          </h2>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-xs font-medium" style={{ color: ui.textSecondary }}>
                {t('licenseDetail.photoLabel', 'License photo (optional)')}
              </p>

              <button type="button" onClick={() => photoInputRef.current?.click()} className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold" style={subtleButtonStyle}>
                <CameraIcon className="h-4 w-4" />
                {t('licenseDetail.addPhoto', 'Add photo')}
              </button>
            </div>

            <input
              ref={photoInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={onPickLicensePhoto}
            />

            {licensePhotoPreview ? (
              <button
                type="button"
                onClick={() => setPhotoLightboxOpen(true)}
                className="relative w-full overflow-hidden rounded-2xl"
                style={panelStyle}
              >
                <img src={licensePhotoPreview} alt="License preview" className="h-44 w-full object-cover" />
              </button>
            ) : (
              <div className="rounded-2xl border border-dashed p-4 text-xs" style={{ ...panelStyle, color: ui.textSecondary }}>
                Take a quick photo of your license. We’ll store it privately on your account.
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-medium" style={{ color: ui.textSecondary }}>
                {t('licenseDetail.fieldState', 'State / Region')}
              </label>
              <input type="text" value={formState.state} onChange={(e) => setFormState((prev) => ({ ...prev, state: e.target.value }))} className="w-full rounded-xl px-3 py-2 text-sm" style={inputStyle} placeholder="California" />
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium" style={{ color: ui.textSecondary }}>
                {t('licenseDetail.fieldType', 'License type')}
              </label>
              <input type="text" value={formState.type} onChange={(e) => setFormState((prev) => ({ ...prev, type: e.target.value }))} className="w-full rounded-xl px-3 py-2 text-sm" style={inputStyle} placeholder="Annual Resident Sport Fishing" />
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium" style={{ color: ui.textSecondary }}>
                {t('licenseDetail.fieldNumber', 'License / permit number')}
              </label>
              <input type="text" value={formState.licenseNumber} onChange={(e) => setFormState((prev) => ({ ...prev, licenseNumber: e.target.value }))} className="w-full rounded-xl px-3 py-2 text-sm" style={inputStyle} placeholder="CA2024-12345" />
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium" style={{ color: ui.textSecondary }}>
                {t('licenseDetail.fieldExpiry', 'Expiration date')}
              </label>
              <input type="date" value={formState.expiryDate} onChange={(e) => setFormState((prev) => ({ ...prev, expiryDate: e.target.value }))} className="w-full rounded-xl px-3 py-2 text-sm" style={inputStyle} />
            </div>
          </div>

          <button
            type="button"
            onClick={async () => {
              const ok = await handleSave();
              if (ok) setIsEditing(false);
            }}
            disabled={saving || isUploadingPhoto}
            className="w-full rounded-xl py-3 text-sm font-bold disabled:opacity-60"
            style={goldButtonStyle}
          >
            {saving || isUploadingPhoto ? 'Saving…' : t('licenseDetail.save', 'Save license')}
          </button>
        </div>

        {photoLightboxOpen && licensePhotoPreview ? (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 p-4" onClick={() => setPhotoLightboxOpen(false)}>
            <img src={licensePhotoPreview} alt="License full" className="max-h-[85vh] max-w-[95vw] rounded-2xl shadow-2xl" onClick={(e) => e.stopPropagation()} />
          </div>
        ) : null}

        <div className="h-4" />
      </div>
    );
  }

  const license = activeLicense;
  const savedPhoto = persistedPhotoUrl;

  return (
    <div className="flex h-full flex-col space-y-4 p-4 roddo-license-polish" data-roddo-polish-lane="2" style={pageStyle}>
      <header className="mb-1 flex items-center justify-between">
        <button type="button" onClick={onBack} className="rounded-full px-3 py-2 text-xs font-semibold" style={subtleButtonStyle}>
          Back
        </button>

        <button type="button" onClick={openEditModal} className="rounded-full px-4 py-2 text-xs font-bold" style={goldButtonStyle}>
          Edit
        </button>
      </header>

      <div className="relative mx-auto w-full max-w-md overflow-hidden rounded-[20px] p-6" style={strongPanelStyle}>
        <div className="pointer-events-none absolute inset-0 opacity-30" style={{ background: isDark ? 'radial-gradient(circle at 0 0, rgba(255,255,255,0.10) 0, transparent 50%), radial-gradient(circle at 100% 0, rgba(255,255,255,0.10) 0, transparent 50%)' : 'radial-gradient(circle at 0 0, rgba(255,255,255,0.40) 0, transparent 50%), radial-gradient(circle at 100% 0, rgba(255,255,255,0.40) 0, transparent 50%)' }} />

        <div className="relative z-10">
          <div className="flex items-center justify-between border-b pb-3" style={{ borderColor: ui.border }}>
            <div>
              <p className="text-[10px] uppercase tracking-[0.18em]" style={{ color: ui.textSecondary }}>
                {t('licenseDetail.issuer', 'Recreational Fishing License')}
              </p>
              <p className="mt-1 text-xl font-black" style={{ color: ui.textPrimary }}>
                {(license as any).jurisdictionState || (license as any).state || 'State License'}
              </p>
            </div>

            <span className="rounded-full px-3 py-1 text-[11px] font-bold" style={{ background: 'rgba(52,211,153,0.18)', color: '#16a34a', border: '1px solid rgba(52,211,153,0.28)' }}>
              Active
            </span>
          </div>

          <div className="mt-4 flex items-center gap-3">
            {avatarSrc ? (
              <img src={avatarSrc} alt={holderName} className="h-14 w-14 rounded-full object-cover" style={{ border: `2px solid ${ui.border}` }} />
            ) : (
              <div className="flex h-14 w-14 items-center justify-center rounded-full font-black" style={{ background: ui.surfaceBg ?? ui.cardBgMuted ?? ui.cardBg, color: ui.textPrimary }}>
                {(holderName || 'U').slice(0, 1).toUpperCase()}
              </div>
            )}

            <div className="min-w-0">
              <p className="truncate text-xs uppercase tracking-[0.15em]" style={{ color: ui.textSecondary }}>
                Holder
              </p>
              <p className="truncate text-lg font-extrabold" style={{ color: ui.textPrimary }}>
                {holderName}
              </p>
            </div>
          </div>

          <div className="mt-5 grid grid-cols-2 gap-3 text-sm">
            <div className="rounded-2xl p-3" style={panelStyle}>
              <p className="text-[10px] uppercase tracking-[0.16em]" style={{ color: ui.textSecondary }}>License #</p>
              <p className="mt-1 font-mono font-semibold" style={{ color: ui.textPrimary }}>{(license as any).licenseNumber || '-'}</p>
            </div>
            <div className="rounded-2xl p-3" style={panelStyle}>
              <p className="text-[10px] uppercase tracking-[0.16em]" style={{ color: ui.textSecondary }}>Expires</p>
              <p className="mt-1 font-semibold" style={{ color: ui.textPrimary }}>{formatDate((license as any).expiryDate ?? (license as any).endDate ?? null)}</p>
            </div>
            <div className="rounded-2xl p-3" style={panelStyle}>
              <p className="text-[10px] uppercase tracking-[0.16em]" style={{ color: ui.textSecondary }}>Type</p>
              <p className="mt-1 font-semibold" style={{ color: ui.textPrimary }}>{(license as any).type || 'Recreational Sport Fishing'}</p>
            </div>
            <div className="rounded-2xl p-3" style={panelStyle}>
              <p className="text-[10px] uppercase tracking-[0.16em]" style={{ color: ui.textSecondary }}>Issued</p>
              <p className="mt-1 font-semibold" style={{ color: ui.textPrimary }}>{formatDate((license as any).startDate ?? (license as any).purchasedAt ?? null)}</p>
            </div>
          </div>

          <div className="mt-5 space-y-3">
            <button type="button" onClick={openGovSite} disabled={isFindingLocalLicense} className="w-full rounded-2xl px-4 py-3 text-sm font-bold disabled:opacity-60" style={mintButtonStyle}>
              {isFindingLocalLicense ? 'Finding your local license…' : 'Open official purchase / renewal site'}
            </button>

            {savedPhoto ? (
              <button type="button" onClick={() => setPhotoLightboxOpen(true)} className="w-full overflow-hidden rounded-2xl" style={panelStyle}>
                <img src={savedPhoto} alt="Stored license" className="h-52 w-full object-cover" />
              </button>
            ) : (
              <div className="rounded-2xl border border-dashed p-4 text-sm" style={{ ...panelStyle, color: ui.textSecondary }}>
                No license photo stored yet.
              </div>
            )}
          </div>
        </div>
      </div>

      {isEditing ? (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/45 p-4 backdrop-blur-sm" onClick={closeEditModal}>
          <div className="w-full max-w-lg rounded-[20px] p-5" style={strongPanelStyle} onClick={(e) => e.stopPropagation()}>
            <div className="mb-4 flex items-center justify-between">
              <div>
                <p className="text-[11px] uppercase tracking-[0.18em]" style={{ color: ui.textSecondary }}>
                  License editor
                </p>
                <h2 className="text-xl font-extrabold" style={{ color: ui.textPrimary }}>
                  Update license
                </h2>
              </div>
              <button type="button" onClick={closeEditModal} className="rounded-full p-2" style={subtleButtonStyle}>
                <XIcon className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-4 rounded-[24px] p-4" style={panelStyle}>
                <div className="flex items-center justify-between">
                  <label className="text-xs font-semibold" style={{ color: ui.textSecondary }}>
                    License photo
                  </label>
                  <button type="button" onClick={() => photoInputRef.current?.click()} className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold" style={subtleButtonStyle}>
                    <CameraIcon className="h-4 w-4" />
                    {licensePhotoPreview ? 'Change photo' : 'Add photo'}
                  </button>
                </div>

                <input ref={photoInputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onPickLicensePhoto} />

                {licensePhotoPreview ? (
                  <button type="button" onClick={() => setPhotoLightboxOpen(true)} className="overflow-hidden rounded-2xl" style={panelStyle}>
                    <img src={licensePhotoPreview} alt="License preview" className="h-44 w-full object-cover" />
                  </button>
                ) : (
                  <div className="rounded-2xl border border-dashed p-4 text-xs" style={{ ...panelStyle, color: ui.textSecondary }}>
                    Add a license photo for quick field access.
                  </div>
                )}

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-xs font-medium" style={{ color: ui.textSecondary }}>State / Region</label>
                    <input type="text" value={formState.state} onChange={(e) => setFormState((prev) => ({ ...prev, state: e.target.value }))} className="w-full rounded-xl px-3 py-2 text-sm" style={inputStyle} />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium" style={{ color: ui.textSecondary }}>License type</label>
                    <input type="text" value={formState.type} onChange={(e) => setFormState((prev) => ({ ...prev, type: e.target.value }))} className="w-full rounded-xl px-3 py-2 text-sm" style={inputStyle} />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium" style={{ color: ui.textSecondary }}>License / permit number</label>
                    <input type="text" value={formState.licenseNumber} onChange={(e) => setFormState((prev) => ({ ...prev, licenseNumber: e.target.value }))} className="w-full rounded-xl px-3 py-2 text-sm" style={inputStyle} />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium" style={{ color: ui.textSecondary }}>Expiration date</label>
                    <input type="date" value={formState.expiryDate} onChange={(e) => setFormState((prev) => ({ ...prev, expiryDate: e.target.value }))} className="w-full rounded-xl px-3 py-2 text-sm" style={inputStyle} />
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-2">
                  <button type="button" onClick={closeEditModal} className="rounded-xl px-3 py-2 text-sm font-medium" style={subtleButtonStyle}>
                    Cancel
                  </button>
                  <button type="button" onClick={async () => { const ok = await handleSave(); if (ok) closeEditModal(); }} disabled={saving || isUploadingPhoto} className="rounded-xl px-3 py-2 text-sm font-semibold disabled:opacity-60" style={goldButtonStyle}>
                    {saving || isUploadingPhoto ? 'Saving…' : 'Save changes'}
                  </button>
                </div>
            </div>
          </div>
        </div>
      ) : null}

      {photoLightboxOpen && licensePhotoPreview ? (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 p-4" onClick={() => setPhotoLightboxOpen(false)}>
          <img src={licensePhotoPreview} alt="License full" className="max-h-[85vh] max-w-[95vw] rounded-2xl shadow-2xl" onClick={(e) => e.stopPropagation()} />
        </div>
      ) : null}
    </div>
  );
};

export default LicenseDetailView;
